import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cloud,
  Users,
  Settings,
  DollarSign,
  Phone,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  Check,
  X,
  Clock,
  TrendingUp,
  Filter,
  Star,
  Mail,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_API_KEY = 'ruonn-admin-key-2024';

// Admin API headers
const adminHeaders = {
  'X-Admin-Key': ADMIN_API_KEY,
  'Content-Type': 'application/json'
};

const CloudServicePage = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  
  // Users data
  const [users, setUsers] = useState([]);
  const [globalPricing, setGlobalPricing] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCustomOnly, setFilterCustomOnly] = useState(false);
  
  // Pricing data
  const [pricingSettings, setPricingSettings] = useState({
    sttCostPerMinute: 0.006,
    llmCostPer1kTokens: 0.0006,
    ttsCostPerCharacter: 0.00018,
    realtimeAudioCostPerMinute: 0.09,
    twilioCostPerMinute: 0.0085,
    overheadPerMinute: 0.01,
    pricePerMinute: 0.20,
    subscriptionPrice: 10.0,
    freeMinutesIncluded: 10,
  });
  
  // Tenant pricing edit
  const [editingTenant, setEditingTenant] = useState(null);
  const [editForm, setEditForm] = useState({
    pricePerMinute: '',
    subscriptionPrice: '',
    freeMinutesIncluded: '',
    customNote: ''
  });
  
  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    fromEmail: '',
    fromName: 'Ruonn Cloud',
    smtpHost: '',
    smtpPort: 465,
    smtpUsername: '',
    smtpPassword: '',
    useTls: true,
    replyTo: '',
    smtpPasswordSet: false,
  });
  const [emailLogs, setEmailLogs] = useState([]);
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'pricing') {
      fetchPricing();
    } else if (activeTab === 'email') {
      fetchEmailSettings();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/cloud/users`);
      if (response.data.success) {
        setUsers(response.data.data.users);
        setGlobalPricing(response.data.data.globalPricing || {});
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/cloud/pricing/global`);
      if (response.data.success) {
        setPricingSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      toast.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantPricing = async (tenantId) => {
    try {
      const response = await axios.get(`${API}/admin/cloud/pricing/tenant/${tenantId}`);
      if (response.data.success) {
        const override = response.data.data.override;
        setEditForm({
          pricePerMinute: override?.pricePerMinute ?? '',
          subscriptionPrice: override?.subscriptionPrice ?? '',
          freeMinutesIncluded: override?.freeMinutesIncluded ?? '',
          customNote: override?.customNote ?? ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch tenant pricing:', error);
    }
  };

  const savePricing = async () => {
    try {
      await axios.put(`${API}/admin/cloud/pricing/global`, pricingSettings);
      toast.success('Pricing settings saved');
    } catch (error) {
      console.error('Failed to save pricing:', error);
      toast.error('Failed to save pricing');
    }
  };

  const openEditModal = async (user) => {
    setEditingTenant(user);
    await fetchTenantPricing(user.tenantId);
  };

  const saveTenantPricing = async () => {
    try {
      const payload = {
        tenantId: editingTenant.tenantId,
        pricePerMinute: editForm.pricePerMinute !== '' ? parseFloat(editForm.pricePerMinute) : null,
        subscriptionPrice: editForm.subscriptionPrice !== '' ? parseFloat(editForm.subscriptionPrice) : null,
        freeMinutesIncluded: editForm.freeMinutesIncluded !== '' ? parseInt(editForm.freeMinutesIncluded) : null,
        customNote: editForm.customNote || null
      };
      
      await axios.put(`${API}/admin/cloud/pricing/tenant/${editingTenant.tenantId}`, payload);
      toast.success('Custom pricing saved');
      setEditingTenant(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to save tenant pricing:', error);
      toast.error('Failed to save custom pricing');
    }
  };

  const removeTenantPricing = async (tenantId) => {
    if (!window.confirm('Remove custom pricing and revert to global rates?')) return;
    try {
      await axios.delete(`${API}/admin/cloud/pricing/tenant/${tenantId}`);
      toast.success('Custom pricing removed');
      fetchUsers();
    } catch (error) {
      console.error('Failed to remove tenant pricing:', error);
      toast.error('Failed to remove custom pricing');
    }
  };

  const deleteTenant = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to delete "${tenantName}"?\n\nThis will permanently delete:\n- The tenant account\n- All business profiles\n- All subscriptions\n- All call history\n- All phone numbers\n\nThis action cannot be undone!`)) return;
    
    try {
      await axios.delete(`${API}/admin/cloud/tenant/${tenantId}`, { headers: adminHeaders });
      toast.success('Tenant deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  // Email Settings Functions
  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/email/settings`, { headers: adminHeaders }),
        axios.get(`${API}/admin/email/logs?limit=20`, { headers: adminHeaders })
      ]);
      
      if (settingsRes.data.success) {
        const data = settingsRes.data.data;
        setEmailSettings({
          enabled: data.enabled || false,
          fromEmail: data.fromEmail || '',
          fromName: data.fromName || 'Ruonn Cloud',
          smtpHost: data.smtpHost || '',
          smtpPort: data.smtpPort || 465,
          smtpUsername: data.smtpUsername || '',
          smtpPassword: '',
          useTls: data.useTls !== false,
          replyTo: data.replyTo || '',
          smtpPasswordSet: data.smtpPasswordSet || false,
        });
      }
      
      if (logsRes.data.success) {
        setEmailLogs(logsRes.data.data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
      toast.error('Failed to fetch email settings');
    } finally {
      setLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      const payload = {
        enabled: emailSettings.enabled,
        fromEmail: emailSettings.fromEmail,
        fromName: emailSettings.fromName,
        smtpHost: emailSettings.smtpHost,
        smtpPort: emailSettings.smtpPort,
        smtpUsername: emailSettings.smtpUsername,
        useTls: emailSettings.useTls,
        replyTo: emailSettings.replyTo,
      };
      
      // Only include password if it was changed
      if (emailSettings.smtpPassword) {
        payload.smtpPassword = emailSettings.smtpPassword;
      }
      
      await axios.put(`${API}/admin/email/settings`, payload, { headers: adminHeaders });
      toast.success('Email settings saved');
      fetchEmailSettings();
    } catch (error) {
      console.error('Failed to save email settings:', error);
      toast.error('Failed to save email settings');
    }
  };

  const testEmailConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await axios.post(`${API}/admin/email/test`, {}, { headers: adminHeaders });
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.data?.error || 'Connection failed');
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setSendingTestEmail(true);
      const response = await axios.post(`${API}/admin/email/send-test`, {
        toEmail: testEmailAddress
      }, { headers: adminHeaders });
      
      if (response.data.success) {
        toast.success('Test email sent!');
        setTestEmailAddress('');
        fetchEmailSettings(); // Refresh logs
      } else {
        toast.error(response.data.data?.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const calculateEstimatedCost = () => {
    // Realtime API: single audio cost (input + output combined)
    const realtimeCost = pricingSettings.realtimeAudioCostPerMinute || 0.09;
    const twilioCost = pricingSettings.twilioCostPerMinute || 0.014;
    const overhead = pricingSettings.overheadPerMinute || 0.01;
    const total = realtimeCost + twilioCost + overhead;
    return {
      realtime: realtimeCost,
      twilio: twilioCost,
      overhead: overhead,
      total: total,
      margin: (pricingSettings.pricePerMinute || 0) - total
    };
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.tenantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterCustomOnly) {
      return matchesSearch && user.hasCustomPricing;
    }
    return matchesSearch;
  });

  const costEstimate = calculateEstimatedCost();

  // Helper to show if value differs from global
  const isDifferent = (value, globalValue) => {
    return value !== undefined && value !== null && value !== globalValue;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Cloud className="w-7 h-7 text-purple-400" />
            Cloud Service
          </h1>
          <p className="text-gray-400 mt-1">Manage Voice Agent users and pricing</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'users'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          Users
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'pricing'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          <Settings className="w-4 h-4" />
          Price Settings
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            activeTab === 'email'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:text-white'
          }`}
        >
          <Mail className="w-4 h-4" />
          Email Settings
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Search, Filter and Refresh */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <button
              onClick={() => setFilterCustomOnly(!filterCustomOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                filterCustomOnly
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Star className="w-4 h-4" />
              Custom Pricing Only
            </button>
            
            <button
              onClick={fetchUsers}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Global Pricing Reference */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center gap-6 text-sm">
            <span className="text-gray-400">Standard Rates:</span>
            <span className="text-white">
              <span className="text-gray-500">Subscription:</span> ${globalPricing.subscriptionPrice?.toFixed(2) || '10.00'}
            </span>
            <span className="text-white">
              <span className="text-gray-500">Free Min:</span> {globalPricing.freeMinutesIncluded || 10}
            </span>
            <span className="text-white">
              <span className="text-gray-500">Per Min:</span> ${globalPricing.pricePerMinute?.toFixed(2) || '0.20'}
            </span>
          </div>

          {/* Users Table */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[1300px]">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">User</th>
                  <th className="text-center text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-center text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Profiles</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Minutes</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Billable</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Free Min</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">$/Min</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Sub Price</th>
                  <th className="text-center text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Phone #</th>
                  <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Total</th>
                  <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Notes</th>
                  <th className="text-center text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                      {filterCustomOnly ? 'No users with custom pricing' : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.tenantId} className={`hover:bg-gray-800/50 transition-colors ${user.hasCustomPricing ? 'bg-purple-900/10' : ''}`}>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-white flex items-center gap-2">
                            {user.tenantName}
                            {user.hasCustomPricing && (
                              <Star className="w-3 h-3 text-purple-400" fill="currentColor" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          user.subscriptionStatus === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : user.subscriptionStatus === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {user.subscriptionStatus === 'active' ? (
                            <Check className="w-3 h-3" />
                          ) : user.subscriptionStatus === 'pending' ? (
                            <Clock className="w-3 h-3" />
                          ) : (
                            <X className="w-3 h-3" />
                          )}
                          {user.subscriptionStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-white font-medium">
                          <span className="text-green-400">{user.activeSubscriptions}</span>
                          <span className="text-gray-500"> / </span>
                          <span>{user.totalProfiles}</span>
                        </div>
                        <div className="text-xs text-gray-500">active / total</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-white font-medium">{user.totalMinutes}</span>
                        <span className="text-gray-500 text-xs ml-1">({user.totalCalls} calls)</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={user.billableMinutes > 0 ? 'text-amber-400' : 'text-gray-400'}>
                          {user.billableMinutes}
                        </span>
                        {user.billableMinutes > 0 && (
                          <div className="text-xs text-gray-500">${user.usageCost?.toFixed(2)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={isDifferent(user.freeMinutesPerProfile, globalPricing.freeMinutesIncluded) 
                          ? 'text-purple-400 font-medium' 
                          : 'text-gray-400'
                        }>
                          {user.freeMinutesIncluded}
                        </span>
                        {user.activeSubscriptions > 1 && (
                          <div className="text-xs text-gray-500">
                            ({user.freeMinutesPerProfile} × {user.activeSubscriptions})
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={isDifferent(user.pricePerMinute, globalPricing.pricePerMinute) 
                          ? 'text-purple-400 font-medium' 
                          : 'text-gray-400'
                        }>
                          ${user.pricePerMinute?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={isDifferent(user.subscriptionPricePerProfile, globalPricing.subscriptionPrice) 
                          ? 'text-purple-400 font-medium' 
                          : 'text-gray-400'
                        }>
                          ${user.subscriptionPrice?.toFixed(2)}
                        </span>
                        {user.activeSubscriptions > 1 && (
                          <div className="text-xs text-gray-500">
                            (${user.subscriptionPricePerProfile} × {user.activeSubscriptions})
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.phoneCount > 0 ? (
                          <div className="text-xs">
                            <div className="text-white">
                              {user.phoneCount > 1 ? `${user.phoneCount} numbers` : user.phoneNumber}
                            </div>
                            <div className="text-amber-400">+${user.phoneNumberCost?.toFixed(2)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-green-400 font-medium">${user.totalCost?.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3 text-left max-w-[150px]">
                        <span className="text-gray-400 text-sm truncate block" title={user.customNote}>
                          {user.customNote || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-400 hover:text-white transition-colors"
                            title="Edit pricing"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {user.hasCustomPricing && (
                            <button
                              onClick={() => removeTenantPricing(user.tenantId)}
                              className="p-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 rounded text-yellow-400 transition-colors"
                              title="Remove custom pricing"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteTenant(user.tenantId, user.tenantName)}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 transition-colors"
                            title="Delete tenant"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Tenant Pricing Modal */}
          {editingTenant && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Custom Pricing for {editingTenant.tenantName}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Leave empty to use standard (global) pricing for that field.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Monthly Subscription ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={`Standard: $${globalPricing.subscriptionPrice?.toFixed(2) || '10.00'}`}
                      value={editForm.subscriptionPrice}
                      onChange={(e) => setEditForm({...editForm, subscriptionPrice: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Free Minutes Included
                    </label>
                    <input
                      type="number"
                      placeholder={`Standard: ${globalPricing.freeMinutesIncluded || 10} min`}
                      value={editForm.freeMinutesIncluded}
                      onChange={(e) => setEditForm({...editForm, freeMinutesIncluded: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Price per Minute ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={`Standard: $${globalPricing.pricePerMinute?.toFixed(2) || '0.20'}`}
                      value={editForm.pricePerMinute}
                      onChange={(e) => setEditForm({...editForm, pricePerMinute: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Note (e.g., "Early adopter discount")
                    </label>
                    <input
                      type="text"
                      placeholder="Optional note..."
                      value={editForm.customNote}
                      onChange={(e) => setEditForm({...editForm, customNote: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setEditingTenant(null)}
                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTenantPricing}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Service Costs (What You Pay) */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-red-400" />
              Service Costs (Your Cost)
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              What you pay to service providers per unit.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  OpenAI Realtime API - per minute
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.001"
                    value={pricingSettings.realtimeAudioCostPerMinute ?? 0.09}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      realtimeAudioCostPerMinute: parseFloat(e.target.value) || 0
                    })}
                    className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  gpt-4o-mini-realtime-preview audio input + output combined (~$0.09/min typical)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Twilio - per minute
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.0001"
                    value={pricingSettings.twilioCostPerMinute}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      twilioCostPerMinute: parseFloat(e.target.value) || 0
                    })}
                    className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Platform Overhead - per minute
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.0001"
                    value={pricingSettings.overheadPerMinute}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      overheadPerMinute: parseFloat(e.target.value) || 0
                    })}
                    className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Customer Pricing (What You Charge) */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Customer Pricing
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              What you charge customers for the Voice Agent service.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Monthly Subscription
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={pricingSettings.subscriptionPrice}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      subscriptionPrice: parseFloat(e.target.value) || 0
                    })}
                    className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Free Minutes Included
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={pricingSettings.freeMinutesIncluded}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      freeMinutesIncluded: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">min</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Price Per Minute (after free tier)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={pricingSettings.pricePerMinute}
                    onChange={(e) => setPricingSettings({
                      ...pricingSettings,
                      pricePerMinute: parseFloat(e.target.value) || 0
                    })}
                    className="w-full pl-8 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              {/* Cost Breakdown Summary */}
              <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="text-sm font-medium text-white mb-3">Cost Breakdown (per minute)</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">OpenAI Realtime API</span>
                    <span className="text-red-400">${costEstimate.realtime.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Twilio</span>
                    <span className="text-red-400">${costEstimate.twilio.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Overhead</span>
                    <span className="text-red-400">${costEstimate.overhead.toFixed(4)}</span>
                  </div>
                  <div className="border-t border-gray-600 pt-2 mt-2">
                    <div className="flex justify-between font-medium">
                      <span className="text-white">Total Cost</span>
                      <span className="text-red-400">${costEstimate.total.toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between font-medium mt-1">
                      <span className="text-white">You Charge</span>
                      <span className="text-green-400">${pricingSettings.pricePerMinute.toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between font-medium mt-1">
                      <span className="text-white">Margin</span>
                      <span className={costEstimate.margin >= 0 ? 'text-green-400' : 'text-red-400'}>
                        ${costEstimate.margin.toFixed(4)}
                        <span className="text-xs ml-1">
                          ({((costEstimate.margin / pricingSettings.pricePerMinute) * 100).toFixed(0)}%)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={savePricing}
                className="w-full mt-4 px-4 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg text-white font-medium transition-colors"
              >
                Save Pricing Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Settings Tab */}
      {activeTab === 'email' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SMTP Configuration */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              SMTP Configuration
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Configure email settings for billing notifications.
            </p>
            
            <div className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <div className="text-white font-medium">Enable Email Notifications</div>
                  <div className="text-sm text-gray-400">Send billing emails to customers</div>
                </div>
                <button
                  onClick={() => setEmailSettings({...emailSettings, enabled: !emailSettings.enabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    emailSettings.enabled ? 'bg-purple-600' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      emailSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  From Email
                </label>
                <input
                  type="email"
                  value={emailSettings.fromEmail}
                  onChange={(e) => setEmailSettings({...emailSettings, fromEmail: e.target.value})}
                  placeholder="no-reply@example.com"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  From Name
                </label>
                <input
                  type="text"
                  value={emailSettings.fromName}
                  onChange={(e) => setEmailSettings({...emailSettings, fromName: e.target.value})}
                  placeholder="Ruonn Cloud"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={emailSettings.smtpHost}
                    onChange={(e) => setEmailSettings({...emailSettings, smtpHost: e.target.value})}
                    placeholder="mail.example.com"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    value={emailSettings.smtpPort}
                    onChange={(e) => setEmailSettings({...emailSettings, smtpPort: parseInt(e.target.value) || 465})}
                    placeholder="465"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  SMTP Username
                </label>
                <input
                  type="text"
                  value={emailSettings.smtpUsername}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpUsername: e.target.value})}
                  placeholder="username@example.com"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  SMTP Password
                  {emailSettings.smtpPasswordSet && (
                    <span className="ml-2 text-xs text-green-400">(Password is set)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={emailSettings.smtpPassword}
                  onChange={(e) => setEmailSettings({...emailSettings, smtpPassword: e.target.value})}
                  placeholder={emailSettings.smtpPasswordSet ? "••••••••" : "Enter password"}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Reply-To Email (optional)
                </label>
                <input
                  type="email"
                  value={emailSettings.replyTo}
                  onChange={(e) => setEmailSettings({...emailSettings, replyTo: e.target.value})}
                  placeholder="support@example.com"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="useTls"
                  checked={emailSettings.useTls}
                  onChange={(e) => setEmailSettings({...emailSettings, useTls: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="useTls" className="text-sm text-gray-400">
                  Use TLS/SSL (recommended for port 465)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={testEmailConnection}
                  disabled={testingConnection || !emailSettings.smtpHost}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg text-white transition-colors flex items-center justify-center gap-2"
                >
                  {testingConnection ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Test Connection
                </button>
                <button
                  onClick={saveEmailSettings}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white transition-colors"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>

          {/* Test & Logs */}
          <div className="space-y-6">
            {/* Send Test Email */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-green-400" />
                Send Test Email
              </h3>
              
              <div className="flex gap-3">
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="test@example.com"
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                />
                <button
                  onClick={sendTestEmail}
                  disabled={sendingTestEmail || !emailSettings.enabled}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg text-white transition-colors flex items-center gap-2"
                >
                  {sendingTestEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send
                </button>
              </div>
              
              {!emailSettings.enabled && (
                <p className="mt-2 text-sm text-yellow-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Enable email notifications to send test emails
                </p>
              )}
            </div>

            {/* Email Logs */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-gray-400" />
                  Recent Email Logs
                </h3>
                <button
                  onClick={fetchEmailSettings}
                  className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-400 hover:text-white transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {emailLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No email logs yet</p>
                ) : (
                  emailLogs.map((log, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border ${
                        log.status === 'sent'
                          ? 'bg-green-900/20 border-green-500/30'
                          : 'bg-red-900/20 border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {log.status === 'sent' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-white text-sm">{log.to}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(log.sentAt || log.failedAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-400 truncate">
                        {log.subject}
                      </div>
                      {log.error && (
                        <div className="mt-1 text-xs text-red-400">
                          Error: {log.error}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudServicePage;
